import { useState } from 'react'
import clsx from 'clsx'
import { Icon } from '@iconify/react'
import extractDomain from 'extract-domain'
import type {
  HeadingOutline as THeadingOutline,
  ResearchReportDocument,
  PageStats,
} from '@pepper/utils/src/types/seo'
import { getReadabilityStatement } from '@pepper/utils/src/function/get_readability'
import { Collapse, Empty, HeadingOutline, Panel, Text } from '@pepper/ui'

import LoadingCompetitors from './LoadingCompetitors'

import styles from './Outline.module.css'

interface Props {
  isGeneratingReport: boolean
  pageStats?: PageStats
  ai_outline?: THeadingOutline[]
  competitors?: ResearchReportDocument['competitors']
}

const ACCORDION_ROTATE = 90

function Outline({
  ai_outline,
  pageStats,
  competitors,
  isGeneratingReport,
}: Props): JSX.Element {
  const [activeKey, setActiveKey] = useState<string | string[]>()

  const defaultActiveKey = ai_outline?.length ? ai_outline[0].text : '1'

  const handlePanelChange = (key: string | string[]) => {
    setActiveKey(key)
  }

  return (
    <div>
      {pageStats ? (
        <section>
          <Collapse
            className={clsx(styles.accordion, styles.collapseWrapper)}
            ghost={true}
            expandIconPosition="right"
            expandIcon={({ isActive }) => (
              <Icon
                icon="tabler:chevron-down"
                width={16}
                height={16}
                rotate={isActive ? ACCORDION_ROTATE : 0}
              />
            )}
            onChange={handlePanelChange}
            activeKey={activeKey}
          >
            <Panel
              key={pageStats.length}
              className={clsx(styles.panel)}
              header={
                <div className={styles.headerWrapper}>
                  <div className={styles.headerTextImgContainer}>
                    <img
                      className={styles.questionImg}
                      alt="Company logo"
                      src={`https://logo.clearbit.com/${extractDomain(
                        pageStats?.url
                      )}`}
                    />

                    <div className={styles.titleWrapper}>
                      <Text maxLines={1} className={styles.headerTitle}>
                        {pageStats.title}
                      </Text>
                      <a
                        href={pageStats.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.headerLink}
                      >
                        <Icon
                          icon="lucide:external-link"
                          width={16}
                          height={16}
                        />
                      </a>
                      <span className={styles.urlBadge}>Your URL</span>
                    </div>
                  </div>
                  <div className={styles.badgeContainer}>
                    <div className={styles.metaDataBadge}>
                      <Icon icon="prime:pencil" width={16} height={16} />
                      <p>{pageStats.length} words</p>
                    </div>
                    <div className={styles.metaDataBadge}>
                      <Icon icon="lucide:book-open" width={16} height={16} />
                      <p
                        dangerouslySetInnerHTML={{
                          __html: getReadabilityStatement(
                            pageStats.reading_ease
                          ).level,
                        }}
                      />
                    </div>
                    {pageStats.element_counts && (
                      <div className={styles.metaDataBadge}>
                        <div className={styles.headingTag}> H </div>
                        <p>{pageStats.element_counts.hx} headings</p>
                      </div>
                    )}
                    {pageStats.element_counts && (
                      <div className={styles.metaDataBadge}>
                        <Icon icon="lucide:link" width={16} height={16} />
                        <p>{pageStats.element_counts.a} links</p>
                      </div>
                    )}
                  </div>
                </div>
              }
            >
              {pageStats.outline ? (
                <HeadingOutline htmlJsonList={pageStats.outline} />
              ) : (
                <Empty />
              )}
            </Panel>
          </Collapse>
        </section>
      ) : null}

      <section>
        <Collapse
          className={clsx(
            styles.accordion,
            styles.borderPurple,
            styles.backGroundPurple
          )}
          expandIconPosition="right"
          expandIcon={({ isActive }) => (
            <Icon
              icon="tabler:chevron-down"
              width={16}
              height={16}
              rotate={isActive ? ACCORDION_ROTATE : 0}
            />
          )}
          ghost={true}
          defaultActiveKey={defaultActiveKey}
          key={ai_outline?.[0]?.text}
        >
          <Panel
            header={
              <div className={styles.headerWrapper}>
                <div className={styles.headerTextImgContainer}>
                  <Icon
                    icon="mdi:stars"
                    width={16}
                    height={16}
                    color="var(--color-purple-700)"
                  />

                  <div>
                    <Text className={styles.headerTitlePurple}>
                      Pepper AI suggested outline
                    </Text>
                  </div>
                </div>
              </div>
            }
            key={defaultActiveKey}
            className={clsx(styles.panel)}
          >
            <HeadingOutline htmlJsonList={ai_outline} />
          </Panel>
        </Collapse>
      </section>

      <section>
        {competitors?.map((competitor, index) => {
          return (
            <Collapse
              key={index}
              className={clsx(styles.accordion, styles.collapseWrapper)}
              ghost={true}
              expandIconPosition="right"
              expandIcon={({ isActive }) => (
                <Icon
                  icon="tabler:chevron-down"
                  width={16}
                  height={16}
                  rotate={isActive ? ACCORDION_ROTATE : 0}
                />
              )}
              onChange={handlePanelChange}
              activeKey={activeKey}
            >
              <Panel
                key={index}
                className={clsx(styles.panel, {
                  [styles.activePanel]: activeKey?.includes(index.toString()),
                })}
                header={
                  <div className={styles.headerWrapper}>
                    <div className={styles.headerTextImgContainer}>
                      <img
                        className={styles.questionImg}
                        alt="Company logo"
                        src={`https://logo.clearbit.com/${extractDomain(
                          competitor.url
                        )}`}
                      />

                      <div className={styles.titleWrapper}>
                        <Text maxLines={1} className={styles.headerTitle}>
                          {competitor.title}
                        </Text>
                        <a
                          href={competitor.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.headerLink}
                        >
                          <Icon
                            icon="lucide:external-link"
                            width={16}
                            height={16}
                          />
                        </a>
                      </div>
                    </div>
                    <div className={styles.badgeContainer}>
                      <div className={styles.metaDataBadge}>
                        <Icon icon="prime:pencil" width={16} height={16} />
                        <p>{competitor.length} words</p>
                      </div>
                      <div className={styles.metaDataBadge}>
                        <Icon icon="lucide:book-open" width={16} height={16} />
                        <p
                          dangerouslySetInnerHTML={{
                            __html: getReadabilityStatement(
                              competitor.reading_ease
                            ).level,
                          }}
                        />
                      </div>
                      {competitor.element_counts && (
                        <div className={styles.metaDataBadge}>
                          <div className={styles.headingTag}> H </div>
                          <p>{competitor.element_counts.hx} headings</p>
                        </div>
                      )}
                      {competitor.element_counts && (
                        <div className={styles.metaDataBadge}>
                          <Icon icon="lucide:link" width={16} height={16} />
                          <p>{competitor.element_counts.a} links</p>
                        </div>
                      )}
                    </div>
                  </div>
                }
              >
                {competitor.outline ? (
                  <HeadingOutline htmlJsonList={competitor.outline} />
                ) : (
                  <Empty />
                )}
              </Panel>
            </Collapse>
          )
        })}
        {!competitors?.length && isGeneratingReport && <LoadingCompetitors />}
      </section>
    </div>
  )
}

export default Outline
